import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { listGiveaways, createGiveaway } from "../services/giveawayService";
import { handleUnauthorized } from "../../utils/apiHelper";
import { apiProblem, fmtEt, isSuperAdmin, showProblem, toastOk } from "./giveawayUtils";
import { StatusPill } from "./GiveawayUi";
import NotAllowedCard from "./NotAllowedCard";

// Giveaways list (2026-10-22). One row per contest page. "New giveaway"
// makes a draft; nothing goes public until it is scheduled or opened from
// its own page, and that is refused until the launch checklist is clear.

const SLUG_RE = /^[a-z0-9-]{3,60}$/;

const GiveawaysListLayer = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ slug: "", name: "", city: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setProblem(null);
    try {
      const res = await listGiveaways();
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      if (e?.response?.status === 401) return handleUnauthorized(navigate);
      setProblem(apiProblem(e, "Could not load the giveaways."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin()) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isSuperAdmin() || problem?.status === 403) {
    return <NotAllowedCard message={problem?.message} />;
  }

  const slugOk = SLUG_RE.test(form.slug);
  const canCreate = slugOk && form.name.trim() && form.city.trim() && !saving;

  const create = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    setSaving(true);
    try {
      const res = await createGiveaway({
        slug: form.slug.trim(),
        name: form.name.trim(),
        city: form.city.trim(),
      });
      toastOk("Draft giveaway created");
      const id = res.data?.data?._id;
      if (id) navigate(`/giveaways/${id}`);
      else {
        setShowCreate(false);
        setForm({ slug: "", name: "", city: "" });
        load();
      }
    } catch (err) {
      if (err?.response?.status === 401) return handleUnauthorized(navigate);
      showProblem(apiProblem(err, "Could not create the giveaway."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card basic-data-table radius-12">
      <div className="card-header d-flex align-items-start justify-content-between flex-wrap gap-2">
        <div>
          <h5 className="card-title mb-1">Giveaways</h5>
          <p className="text-sm text-secondary-light mb-0" style={{ maxWidth: 680 }}>
            Contest pages, their entries and the winner draw. A new giveaway starts as a draft and
            stays hidden until you schedule or open it.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm d-flex align-items-center gap-1"
          onClick={() => setShowCreate((s) => !s)}
        >
          <Icon icon={showCreate ? "mdi:close" : "ic:baseline-plus"} />
          {showCreate ? "Cancel" : "New giveaway"}
        </button>
      </div>
      <div className="card-body">
        {showCreate ? (
          <form className="p-16 radius-8 border mb-24" onSubmit={create}>
            <h6 className="text-md fw-semibold mb-12">New giveaway</h6>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label text-sm fw-semibold">Name</label>
                <input
                  className="form-control"
                  value={form.name}
                  placeholder="Ottawa Nordik Spa giveaway"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label text-sm fw-semibold">City</label>
                <input
                  className="form-control"
                  value={form.city}
                  placeholder="Ottawa"
                  onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label text-sm fw-semibold">Web address name</label>
                <input
                  className="form-control"
                  value={form.slug}
                  placeholder="ottawa-nordik-2026"
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))}
                />
                <div
                  className="text-xs mt-1"
                  style={{ color: form.slug && !slugOk ? "#FA69B9" : undefined }}
                >
                  3 to 60 lowercase letters, numbers or dashes. It becomes part of the page address
                  and cannot be changed later.
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end mt-16">
              <button type="submit" className="btn btn-primary btn-sm" disabled={!canCreate}>
                {saving ? "Creating…" : "Create draft"}
              </button>
            </div>
          </form>
        ) : null}

        {problem ? (
          <div className="alert alert-danger py-2">
            {problem.message}{" "}
            <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={load}>
              Try again
            </button>
          </div>
        ) : null}

        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>Status</th>
                <th>Opens</th>
                <th>Closes</th>
                <th className="text-end">Entries</th>
                <th className="text-end">Valid</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>No giveaways yet. Press "New giveaway" to start one.</td>
                </tr>
              ) : (
                rows.map((g) => (
                  <tr key={g._id}>
                    <td>
                      <Link to={`/giveaways/${g._id}`} className="fw-semibold text-primary-600">
                        {g.name || g.slug}
                      </Link>
                      <div className="text-xs text-secondary-light">{g.slug}</div>
                    </td>
                    <td>{g.city || "Not set"}</td>
                    <td>
                      <StatusPill status={g.status} paused={g.paused} />
                    </td>
                    <td className="text-sm">{g.opensLabel || fmtEt(g.opensAt)}</td>
                    <td className="text-sm">{g.closesLabel || fmtEt(g.closesAt)}</td>
                    <td className="text-end" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {g.counts?.total ?? 0}
                    </td>
                    <td className="text-end" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {g.counts?.valid ?? 0}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GiveawaysListLayer;
